// src/context/CoAContext.js

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "./CompanyContext";

const CoAContext = createContext();

export const useCoA = () => {
  const ctx = useContext(CoAContext);
  if (!ctx) throw new Error("useCoA must be used within a CoAProvider");
  return ctx;
};

export const CoAProvider = ({ children }) => {
  const { companyId, companyRole } = useCompany();
  const [accounts, setAccounts] = useState([]);

  // Fetch accounts for the current company
  const fetchAccounts = useCallback(async () => {
    if (!companyId) {
      setAccounts([]);
      return;
    }

    // Ensure the user is authenticated
    const {
      data: { user },
      error: authErr,
    } = await supabase.auth.getUser();
    if (authErr || !user) {
      console.error("Not authenticated:", authErr);
      setAccounts([]);
      return;
    }

    // Fetch the chart_of_accounts rows
    const { data, error } = await supabase
      .from("chart_of_accounts")
      .select("id, company_id, account_number, name, type, category")
      .eq("company_id", companyId)
      .order("account_number", { ascending: true });

    if (error) {
      console.error("Error fetching COA:", error);
      setAccounts([]);
    } else {
      setAccounts(data || []);
    }
  }, [companyId]);

  // Add or update an account
  const saveAccount = useCallback(
    async (account) => {
      if (companyRole !== "admin") {
        alert("Only admins can save accounts.");
        return null;
      }

      const payload = {
        id: account.id || uuidv4(),
        company_id: companyId,
        account_number: account.account_number,
        name: account.name,
        type: account.type,
        category: account.category,
      };

      const { data, error } = await supabase
        .from("chart_of_accounts")
        .upsert([payload], { onConflict: ["id"] })
        .select("id, company_id, account_number, name, type, category");

      if (error) {
        console.error("Error saving account:", error);
        alert("Could not save account.");
        return null;
      }

      const saved = data[0];
      setAccounts((prev) => {
        const idx = prev.findIndex((a) => a.id === saved.id);
        if (idx !== -1) {
          const copy = [...prev];
          copy[idx] = saved;
          return copy;
        } else {
          return [...prev, saved];
        }
      });

      return saved;
    },
    [companyId, companyRole]
  );

  // Delete an account
  const deleteAccount = useCallback(
    async (accountId) => {
      if (companyRole !== "admin") {
        alert("Only admins can delete accounts.");
        return { success: false, error: "not-authorized" };
      }

      const { error } = await supabase
        .from("chart_of_accounts")
        .delete()
        .eq("id", accountId)
        .eq("company_id", companyId);

      if (error) {
        console.error("Error deleting account:", error);
        alert("Could not delete account.");
        return { success: false, error };
      }

      setAccounts((prev) => prev.filter((a) => a.id !== accountId));
      return { success: true };
    },
    [companyId, companyRole]
  );

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  return (
    <CoAContext.Provider
      value={{ accounts, setAccounts, saveAccount, deleteAccount }}
    >
      {children}
    </CoAContext.Provider>
  );
};
